import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type {
  DeselectRate,
  HotelRoomRateMeta,
  UseHotelRoomRatesSource,
} from "@b2bportal/core-types";

export type HotelRoomRateTransformFn = (
  rates: HotelRoomRateMeta[]
) => HotelRoomRateMeta[];

export interface HotelRoomRateSourcesConfig {
  hooks: UseHotelRoomRatesSource[];
  transform: HotelRoomRateTransformFn;
  setDeselector: (deselector: DeselectRate | null) => void;
  deselect: () => void;
}

const HotelRoomRateSourcesContext =
  createContext<HotelRoomRateSourcesConfig | null>(null);

const noop = () => {};

export const useHotelRoomRateSources = (): HotelRoomRateSourcesConfig => {
  const context = useContext(HotelRoomRateSourcesContext);

  const emptyHotelRoomRateSourcesConfig: HotelRoomRateSourcesConfig = useMemo(
    () => ({
      hooks: [],
      transform: noopTransform,
      setDeselector: noop,
      deselect: noop,
    }),
    []
  );

  // If no context is found, return the empty config, for backwards compatibility
  if (context == null) {
    return emptyHotelRoomRateSourcesConfig;
  }

  return context;
};

export const sortByPriceDifference: HotelRoomRateTransformFn = (
  rates: HotelRoomRateMeta[]
): HotelRoomRateMeta[] =>
  rates.sort(
    (a, b) =>
      (a.priceDifference?.fiat?.value ?? 0) -
      (b.priceDifference?.fiat?.value ?? 0)
  );

export interface HotelRoomRateSourcesProviderProps {
  hooks: UseHotelRoomRatesSource[];
  transform?: HotelRoomRateTransformFn;
  children: React.ReactNode;
}

export function HotelRoomRateSourcesProvider({
  hooks,
  transform = sortByPriceDifference,
  children,
}: HotelRoomRateSourcesProviderProps) {
  const parentContext = useContext(HotelRoomRateSourcesContext);
  const initialHooksRef = useRef(hooks);
  const deselectRef = useRef<DeselectRate | null>(null);

  // Dev-only: ensure hooks never changes (hooks rules safety)
  if (import.meta.env.VITE_ENV !== "production") {
    if (
      hooks !== initialHooksRef.current ||
      hooks.length !== initialHooksRef.current.length
    ) {
      throw new Error(
        "HotelRoomRateSourcesProvider: hooks must be static and cannot change between renders."
      );
    }
  }

  const setDeselector = useCallback((deselector: DeselectRate | null) => {
    deselectRef.current = deselector;
  }, []);

  const deselect = useCallback(() => {
    deselectRef.current?.();
    deselectRef.current = null;
  }, []);

  // Cleanup: deselect on unmount to clear any selection state (e.g., CFAR)
  useEffect(() => {
    return () => {
      deselect();
    };
  }, [deselect]);

  const value = useMemo(
    () => ({ hooks, transform, setDeselector, deselect }),
    [hooks, transform, setDeselector, deselect]
  );

  // First/top provider in the chain wins - if parent exists, pass through
  if (parentContext != null) {
    return <>{children}</>;
  }

  return (
    <HotelRoomRateSourcesContext.Provider value={value}>
      {children}
    </HotelRoomRateSourcesContext.Provider>
  );
}

const noopTransform: HotelRoomRateTransformFn = (rates: HotelRoomRateMeta[]) =>
  rates;
